import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { FontWeightProperty } from 'csstype';
import * as React from 'react';
import { ModalNoticeUI } from '../../../Modal/ModalNoticeUI';
import { Divider } from '../../../Surface/Divider';
import { VerifyType } from '@cube3/common/model/resource-types';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { isMobile } from 'react-device-detect';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { HBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    mainText: {
      marginBottom: theme.customSpacing.margin[16]
    },
    password: {
      marginBottom: theme.customSpacing.margin[16],
      fontWeight: theme.typography.fontWeightBold as FontWeightProperty
    },
    divider: {
      marginBottom: theme.customSpacing.margin[16]
    }
  })
);

interface Properties {
  title: string;
  onCloseEvent: () => void;
  onClickOpenLink?: () => void;
  onCopyLink?: () => void;
  people?: string[];
  /** use for sharelink to notify password changes */
  isResend?: boolean;
  showOpenPersonalLink?: boolean;
  password?: string;
  message?: string;
  verify: VerifyType;
}

const message_share_password =
  'Please share this password with the recipients of this share link now. Cube will not show it again.';
const message_password_changed = `You’ve set a new password on this share link. ${message_share_password}`;

const LinkResentModalUIC: React.FC<Properties> = (props) => {
  const {
    onCloseEvent,
    onClickOpenLink,
    onCopyLink,
    people = undefined,
    title,
    isResend,
    password,
    showOpenPersonalLink = true,
    message,
    verify
  } = props;

  const classes = useStyles();

  const mainText = message
    ? message
    : verify === 'everyone'
      ? 'We will send you a link to the share, that you can forward to relevant parties.'
      : people && people.length === 1
        ? `We will send ${people[0]} a link to the share.`
        : `We will send the selected recipients links to the share.`;

  /*if resending personal link to a member, we don't want to show the password messages */
  const resendLinkMessage = password ? message_password_changed : mainText;

  const sendLinkMessage =
    mainText + `${password ? message_share_password : ''}`;

  return (
    <ModalNoticeUI
      title={title}
      onCloseEvent={onCloseEvent}
      footerRightComponent={
        <div data-cy={'notice'}>
          <Button
            label="Done"
            onClick={onCloseEvent}
            background="primary"
            buttonStyle="solid"
            size={isMobile ? SizeVariants.lg : SizeVariants.md}
          />
        </div>
      }
    >
      <Typography className={classes.mainText} variant={isMobile ? 'lg' : 'md'}>
        {isResend ? resendLinkMessage : sendLinkMessage}
      </Typography>

      {password && (
        <Typography className={classes.password}>{password}</Typography>
      )}

      {password && !isResend && <Divider className={classes.divider} />}

      {!isResend && showOpenPersonalLink && (
        <HBox gap={8}
          style={{
            justifyContent: 'flex-start',
            marginLeft: '-10px',
            marginTop: 8
          }}
        >
          <Button
            label="Open link"
            iconLeft="link"
            onClick={onClickOpenLink}
            background="primary"
            buttonStyle="ghost"
            size={isMobile ? SizeVariants.lg : SizeVariants.md}
          />

          <Button
            label="Copy Link"
            iconLeft="copy"
            onClick={onCopyLink}
            background="primary"
            buttonStyle="ghost"
            size={isMobile ? SizeVariants.lg : SizeVariants.md}
          />
        </HBox>
      )}
    </ModalNoticeUI>
  );
};

/**
 * @summary `Modal Notice` that prompts the user that the link is resent to an X amount of people, and provides the ability to open the sent link.
 */
const LinkResentModalUI = LinkResentModalUIC;
export { LinkResentModalUI };
